import { alpha, Box, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { getAssetsBaseUrl } from '../../../../config/assetsUrl';
import logo from '../../../../assets/logo/logo.png';

// Za glatku reprodukciju na mobitelu dodaj u backend/videos:
// - dji_matej2_mobile.mp4 — ista scena, ali 720p i ~1–2 Mbps (npr. ffmpeg -i dji_matej2.mp4 -vf scale=-2:720 -b:v 1.5M dji_matej2_mobile.mp4)
// - dji_matej2_poster.jpg — jedan kadr (prvi frame) kao JPG za brzi prikaz dok se video učitava
const LANDING_VIDEO_BASE = 'landing';
const assetsUrl = getAssetsBaseUrl();
const mainVideoSrc = `${assetsUrl}/videos/${LANDING_VIDEO_BASE}_web.mp4`;
const mobileVideoSrc = `${assetsUrl}/videos/${LANDING_VIDEO_BASE}_mobile.mp4`;
const posterSrc = `${assetsUrl}/videos/${LANDING_VIDEO_BASE}_poster.jpg`;

const LandingVideo: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [useFallbackSrc, setUseFallbackSrc] = useState(false);

    // Na mobilu koristimo lakšu verziju videa (npr. 720p, niži bitrate) da ne šteka.
    // Ako _mobile.mp4 ne postoji, onError prebaci na glavni video.
    const videoSrc =
        isMobile && !useFallbackSrc ? mobileVideoSrc : mainVideoSrc;

    const handleVideoError = () => {
        if (isMobile) setUseFallbackSrc(true);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100vw',
                marginLeft: 'calc(50% - 50vw)',
                mt: { xs: 0, md: 6 },
                py: { xs: 0, md: 10 },
                minHeight: '100vh',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
                backgroundImage: `
                    radial-gradient(800px 400px at 10% 0%, ${alpha(theme.palette.primary.main, 0.35)} 0%, transparent 60%),
                    radial-gradient(700px 360px at 90% 10%, ${alpha(theme.palette.secondary.main, 0.28)} 0%, transparent 60%),
                    radial-gradient(900px 500px at 50% 100%, ${alpha(theme.palette.primary.dark, 0.3)} 0%, transparent 60%),
                    linear-gradient(180deg, ${alpha("#000", 0.65)} 0%, ${alpha("#000", 0.35)} 40%, ${alpha("#000", 0.65)} 100%)
                `,
                backgroundBlendMode: 'screen, screen, screen, normal',
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0.6) 100%)",
                    zIndex: 0,
                },
            }}
        >
            {/* Na mobilu: lakši video + poster za brži prvi prikaz; na desktopu puna verzija */}
            <Box
                component="video"
                key={`${isMobile ? 'mobile' : 'desktop'}-${videoSrc}`}
                src={videoSrc}
                poster={isMobile ? posterSrc : undefined}
                onError={handleVideoError}
                autoPlay
                muted
                loop
                playsInline
                preload={isMobile ? 'metadata' : 'auto'}
                disablePictureInPicture
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 6, sm: 8 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        textAlign: "center",
                        "@keyframes welcomeFade": {
                            from: { opacity: 1 },
                            to: { opacity: 0 },
                        },
                        "@keyframes logoFadeOut": {
                            from: { opacity: 1 },
                            to: { opacity: 0 },
                        },
                    }}
                >
                    <Box
                        sx={{
                            fontFamily: "Titan One",
                            fontSize: { xs: 28, sm: 34 },
                            letterSpacing: 0.5,
                            color: "#fff",
                            textShadow: "0 6px 18px rgba(0,0,0,0.55)",
                            animation: "welcomeFade 1.2s ease forwards",
                            animationDelay: "6s",
                        }}
                    >
                        Welcome to
                    </Box>
                    <Box
                        component="img"
                        src={logo}
                        alt="Enduro logo"
                        sx={{
                            width: { xs: 280, sm: 460 },
                            // height: { xs: 140, sm: 180 },
                            objectFit: "contain",
                            filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.6))",
                            animation: "logoFadeOut 1.2s ease forwards",
                            animationDelay: "6s",
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default LandingVideo;
